import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { socioApi, type TokenHolding } from "@/api/client";
import { formatTokenAmount } from "@/lib/format";
import { useEffect } from "react";

/**
 * Tela de envio de tokens pra endereço externo.
 *
 * NOTA TÉCNICA: pra enviar tokens da carteira do sócio sem ele precisar
 * de ETH, precisamos do stack ERC-4337 (smart wallet do sócio assina
 * UserOperation localmente com a chave do IndexedDB → bundler relaya
 * → paymaster cobre gas). Hoje o stack AA está implementado mas não
 * ativo nesse projeto Goyaz (faltou habilitar no wizard).
 *
 * Por enquanto, a tela explica isso e mostra como ativar.
 */
export default function Send() {
  // Token principal da carteira (o de maior relevância — primeiro da lista).
  const [token, setToken] = useState<TokenHolding | null>(null);
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");

  const symbol = token?.tokenSymbol ?? "tokens";

  useEffect(() => {
    socioApi.balances().then((r) => setToken(r.tokens[0] ?? null)).catch(() => undefined);
  }, []);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    alert(
      "Envio direto ainda não está disponível.\n\n" +
      "Pra ativar:\n" +
      "1. Admin precisa habilitar Account Abstraction no projeto\n" +
      "2. Ou você pode exportar sua chave em Configurações → Exportar e " +
      "fazer o envio pela MetaMask depois de transferir ETH pra wallet.",
    );
  }

  return (
    <AppShell>
      <div className="max-w-md mx-auto p-6">
        <Link to="/dashboard" className="text-sm underline text-app-muted hover:text-app-accent">
          ← dashboard
        </Link>
        <h1 className="text-2xl font-bold my-4">Enviar {symbol}</h1>

        <div className="app-card border-amber-500/40 bg-amber-500/5 p-4 mb-4">
          <p className="font-semibold mb-1">⚙️ Envio direto em construção</p>
          <p className="text-xs text-app-muted">
            Pra enviar tokens sem precisar de ETH no bolso, o projeto precisa do
            stack <strong>Account Abstraction (AA)</strong> habilitado. Hoje você
            pode <strong>exportar sua chave</strong> e usar uma wallet tradicional
            (MetaMask) com ETH pra fazer o transfer.
          </p>
          <Link to="/settings"
                className="text-app-accent underline text-xs mt-2 inline-block">
            → Exportar chave
          </Link>
        </div>

        <form onSubmit={onSubmit} className="app-card p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Destinatário</label>
            <input type="text" required value={recipient}
                   onChange={(e) => setRecipient(e.target.value)}
                   pattern="0x[a-fA-F0-9]{40}"
                   placeholder="0x..."
                   className="w-full px-3 py-2 font-mono text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Quantidade ({symbol})</label>
            <input type="number" required value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   min={0} step="any"
                   className="w-full px-3 py-2" />
            <p className="text-xs text-app-muted mt-1">
              Saldo disponível: <strong>{token ? formatTokenAmount(token.balance, token.decimals) : "0"} {symbol}</strong>
            </p>
          </div>

          <button type="submit"
                  className="w-full bg-app-accent text-app-accent-text rounded py-2 font-semibold opacity-50"
                  disabled>
            Enviar (em breve)
          </button>
        </form>
      </div>
    </AppShell>
  );
}
